<template lang="pug">
  web-app-layout(:title='"Marketplace"')
    div
      .px-30.py-4(style="padding-top: 60px;")

      .mb-3
        .search-and-pagination-container
          .search-section
            div.input-wrapper.d-flex(style="height: 48px;")
              el-input(
                v-model="state.searchTerm"
                placeholder="Search Stickers"
                style="height: 100%; width:calc(100% - 120px);--el-input-bg-color: #edf3f7;--el-input-hover-border-color:#1fbeaf; --el-input-focus-border-color: #1fbeaf;"
                @input="debouncedSearch"
              ).input-search-filter
                template(#prefix ='')
                  img(
                    src="@/assets/icons/searchIcon.svg",
                    style="font-size: 20px;")
              el-button(
                type="default"
                style="margin-left: 10px; height: 100%;"
                @click="clearSearchAndRefresh"
                class="clear-search-btn"
              ) Clear
              img(
                src="@/assets/icons/filterIcon.svg" style="margin-left:15px; cursor: pointer; height: 20px; filter: grayscale(50%); align-self: center;" @click="openFilterModal"
              )

          .pagination-controls(v-if="state.stickers.length > 0")
            .page-size.d-flex.align-items-center
              span.pe-2 Items per page:
              el-select(
                v-model="state.pageSize"
                @change="handleSizeChange"
                size="small"
                style="width: 70px; --el-color-primary: #1fbeaf; margin-right: 30px;"
                class="marketplace-page-size-select"
              )
                el-option(
                  v-for="size in [5, 10, 20, 50]"
                  :key="size"
                  :label="size"
                  :value="size"
                  class="marketplace-page-size-select-option"
                  :style="state.pageSize === size ? { color: '#1fbeaf' } : {}"
                )

    .no-results.text-center.my-5(v-if="state.stickers.length === 0 && !loading")
      h3 No stickers found
      p(v-if="state.searchTerm") Try a different search term or clear your filters.
      p(v-else) Try clearing your filters.
      el-button(
        @click="clearSearch"
        class="mt-3 clear-search-btn"
      ) Clear Search & Filters

    .loading-container.text-center.my-5(v-if="loading")
      el-icon(:size="50" class="loading-spinner")
        Loading
      p.mt-3 Loading stickers...

    .stickers-grid-wrapper.px-30
      stickers-grid-template(:stickers="state.filteredStickers" v-if="!loading")

    .pagination-wrapper.d-flex.justify-content-center.my-4(v-if="state.stickers.length > 0")
      .simple-pagination.d-flex.align-items-center
        .page-nav.d-flex
          button.page-btn.prev(
            @click="handleCurrentChange(state.currentPage - 1)"
            :disabled="state.currentPage === 1"
          ) Prev
          .page-numbers.mx-2 {{ state.currentPage }}
          button.page-btn.next(
            @click="handleCurrentChange(state.currentPage + 1)"
            :disabled="state.filteredStickers.length < state.pageSize"
          ) Next

    el-dialog(
      width="250px"
      title="Filter Stickers"
      v-model="state.modalVisible"
      @close="resetFilters"
      class="filter-modal"
    )
      label.px-20
        el-checkbox(v-model="state.freeFilter") Free
      label.px-20
        el-checkbox(v-model="state.paidFilter") Paid
      label.px-20
        el-checkbox(v-model="state.campaignFilter") Campaign
      hr
      el-button(type="success" @click="applyFilters" style="width: 100%;") Apply Filters

  </template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, ref, computed,
} from 'vue';
import { useStore } from 'vuex';
import StickersGridTemplate from '../sticker/StickersGridTemplate.vue';
import gqlRequest from './gql-request';
import WebAppLayout from './WebAppLayout.vue';

export default defineComponent({
  name: 'WebAppDashboard',
  components: { WebAppLayout, StickersGridTemplate },
  setup() {
    const {
      route,
      router,
      loading,
      getPublishedStickers,
      getPublishedPublicStickers,
    } = gqlRequest();
    const store = useStore();
    const state = reactive({
      stickers: [] as any[],
      filteredStickers: [] as any[],
      searchTerm: '',
      modalVisible: false,
      freeFilter: false,
      paidFilter: false,
      campaignFilter: false,
      currentPage: 1,
      pageSize: 20,
      totalStickers: 0,
      activeFilters: [] as string[],
    });

    const searchTimeout = ref<number | null>(null);
    const hasFetchedPublishedStickers = computed(() => store.getters['sticker/HAS_FETCHED_PUBLISHED_STICKERS']);

    function debouncedSearch() {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTimeout.value = window.setTimeout(() => {
        searchStickers();
      }, 500);
    }

    async function getStickers() {
      state.stickers = [];
      state.filteredStickers = [];
      loading.value = true;

      try {
        const skip = (state.currentPage - 1) * state.pageSize;
        const activeFilters = [...state.activeFilters];
        if (!activeFilters.includes('Active')) {
          activeFilters.push('Active');
        }

        let stickers;
        if (!hasFetchedPublishedStickers.value
              || state.searchTerm
              || state.activeFilters.length > 1
              || state.currentPage > 1) {
          if (store.getters['users/me'] != null) {
            stickers = await getPublishedStickers(
              state.pageSize, skip, activeFilters, state.searchTerm);
          } else {
            stickers = await getPublishedPublicStickers(
              state.pageSize, skip, activeFilters, state.searchTerm, state.searchTerm);
          }
          if (!state.searchTerm && state.activeFilters.length <= 1 && state.currentPage === 1) {
            store.dispatch('sticker/SET_PUBLISHED_STICKERS', stickers);
          }
        } else {
          stickers = store.getters['sticker/GET_PUBLISHED_STICKERS'];
        }

        state.stickers = stickers;
        state.filteredStickers = stickers;

        updateUrlParams();
      } catch (error) {
        console.error('Error fetching stickers:', error);
      } finally {
        loading.value = false;
      }
    }

    function handleSizeChange(newSize: number) {
      state.pageSize = newSize;
      getStickers();
    }

    function handleCurrentChange(newPage: number) {
      state.currentPage = newPage;
      getStickers();
    }

    async function searchStickers() {
      state.currentPage = 1;
      await getStickers();
    }

    function openFilterModal() {
      state.freeFilter = state.activeFilters.includes('Free');
      state.paidFilter = state.activeFilters.includes('Paid');
      state.campaignFilter = state.activeFilters.includes('Campaign');

      state.modalVisible = true;
    }

    function resetFilters() {
      state.freeFilter = state.activeFilters.includes('Free');
      state.paidFilter = state.activeFilters.includes('Paid');
      state.campaignFilter = state.activeFilters.includes('Campaign');
    }

    async function applyFilters() {
      state.activeFilters = ['Active'];

      if (state.campaignFilter) {
        state.activeFilters.push('Campaign');
      }
      if (state.paidFilter) {
        state.activeFilters.push('Paid');
      }
      if (state.freeFilter) {
        state.activeFilters.push('Free');
      }

      state.currentPage = 1;
      await getStickers();
      state.modalVisible = false;
    }

    async function clearSearch() {
      state.searchTerm = '';
      state.freeFilter = false;
      state.paidFilter = false;
      state.campaignFilter = false;
      state.activeFilters = ['Active'];
      state.currentPage = 1;

      await getStickers();
    }

    function clearSearchAndRefresh() {
      if (!state.searchTerm.length) return;

      state.searchTerm = '';
      searchStickers();
    }

    function clearFilters() {
      state.activeFilters = ['Active'];
      resetFilters();
      searchStickers();
    }

    function removeFilter(filter: string) {
      if (filter === 'Active') return;

      const index = state.activeFilters.indexOf(filter);
      if (index !== -1) {
        state.activeFilters.splice(index, 1);

        if (filter === 'Free') state.freeFilter = false;
        if (filter === 'Paid') state.paidFilter = false;
        if (filter === 'Campaign') state.campaignFilter = false;

        searchStickers();
      }
    }

    function updateUrlParams() {
      const query: {[key: string]: any} = {};

      if (state.searchTerm) query.search = state.searchTerm;
      if (state.activeFilters.length > 0) {
        const displayFilters = state.activeFilters.filter((filter) => filter !== 'Active');
        if (displayFilters.length > 0) {
          query.filters = displayFilters.join(',');
        }
      }
      if (state.currentPage > 1) query.page = state.currentPage;
      if (state.pageSize !== 20) query.size = state.pageSize;

      router.replace({ query }).catch(() => {});
    }

    onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has('search')) {
        state.searchTerm = urlParams.get('search') || '';
      }

      if (urlParams.has('filters')) {
        const filters = (urlParams.get('filters') || '').split(',');
        state.activeFilters = ['Active', ...filters];

        state.freeFilter = filters.includes('Free');
        state.paidFilter = filters.includes('Paid');
        state.campaignFilter = filters.includes('Campaign');
      } else {
        state.activeFilters = ['Active'];
      }

      if (urlParams.has('page')) {
        const page = parseInt(urlParams.get('page') || '1', 10);
        if (!isNaN(page) && page > 0) {
          state.currentPage = page;
        }
      }

      if (urlParams.has('size')) {
        const size = parseInt(urlParams.get('size') || '20', 10);
        if (!isNaN(size) && [5, 10, 20, 50].includes(size)) {
          state.pageSize = size;
        }
      }

      await getStickers();
    });

    return {
      route,
      router,
      loading,
      state,
      searchStickers,
      openFilterModal,
      applyFilters,
      resetFilters,
      handleSizeChange,
      handleCurrentChange,
      clearSearch,
      clearSearchAndRefresh,
      clearFilters,
      removeFilter,
      updateUrlParams,
      debouncedSearch,
    };
  },
});
</script>

  <style scoped>
  /* Mobile-first responsive layout */
  .search-and-pagination-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .search-section {
    width: 100%;
  }

  .pagination-controls {
    align-self: flex-start;
  }

  /* Tablet and up - horizontal layout */
  @media (min-width: 768px) {
    .search-and-pagination-container {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
    }

    .search-section {
      max-width: 600px;
      flex: 1;
      margin-right: 20px;
    }

    .pagination-controls {
      flex-shrink: 0;
      align-self: center;
    }
  }

  .page-size {
    display: flex;
    align-items: center;
    font-weight: 500;
    white-space: nowrap;
  }

  .pagination-wrapper {
    margin-top: 20px;
  }

  .simple-pagination {
    font-size: 16px;
  }

  .page-btn {
    background: transparent;
    border: 2px solid #1fbeaf;
    border-radius: 4px;
    padding: 5px 12px;
    cursor: pointer;
    transition: background-color 0.3s;
  }

  .page-btn:hover:not(:disabled) {
    background: var(--el-color-primary-light-7);
  }

  .clear-search-btn {
    border: 1px solid lightgray;
    color: black;
    border-radius: 4px;
    padding: 5px 12px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
      background-color: #1fbeaf;
      color: #fff;
    }
  }

  .page-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .page-numbers {
    display: flex;
    align-items: center;
    font-size: 18px;
  }

  .filter-modal {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .loading-spinner {
    animation: rotate 2s linear infinite;
    color: #1fbeaf;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .active-filter {
    display: inline-block;
    margin-right: 8px;
  }

  .active-filter .el-tag {
    margin-right: 5px;
  }

  .marketplace-page-size-select-option * .is-selected {
    color: #1fbeaf;
  }

  .stickers-grid-wrapper.px-30 {
    padding-right: 60px !important;
  }
  </style>